import type { PricePreviewResponse } from '@paddle/paddle-js';

export type OfferId = 'personal' | 'family' | 'upgrade';

export type PhotoKeeperOffer = {
  id: OfferId;
  name: string;
  description: string;
  features: string[];
  kind: 'standalone-license' | 'license-upgrade';
  accountLimit: 1 | 3;
  priceId: string;
  cta: string;
};

export type PublicCheckoutConfig = {
  environment: 'sandbox' | 'production';
  clientToken: string;
  countryCode?: string;
  offers: [PhotoKeeperOffer, PhotoKeeperOffer];
  upgrade: PhotoKeeperOffer;
  checkout?: {
    transactionId: string;
    offerId: OfferId;
    expiresAt: string;
  };
};

export type CheckoutFailureView =
  | 'checkout-closed'
  | 'checkout-gone'
  | 'pricing-unavailable';

const transactionIdPattern = /^txn_[a-z\d]{26}$/;
const priceIdPattern = /^pri_[a-z\d]{26}$/;

export function transactionIdFromSearch(search: string): string | undefined {
  const values = new URLSearchParams(search).getAll('_ptxn');
  if (values.length === 0) return undefined;
  if (values.length !== 1 || !transactionIdPattern.test(values[0]!)) {
    throw new Error('invalid_checkout_link');
  }
  return values[0];
}

export function checkoutApiErrorCode(value: unknown): string | undefined {
  if (!isRecord(value) || !isRecord(value.error)) return undefined;
  const code = value.error.code;
  return typeof code === 'string' && /^[a-z][a-z0-9_]{0,63}$/.test(code)
    ? code
    : undefined;
}

export function checkoutFailureView(
  status: number,
  errorCode: string | undefined,
  hasTransactionId: boolean,
): CheckoutFailureView {
  if (status === 409 || (errorCode === 'checkout_unavailable' && hasTransactionId)) {
    return 'checkout-gone';
  }
  if (errorCode === 'checkout_unavailable') return 'checkout-closed';
  return 'pricing-unavailable';
}

export function parsePublicCheckoutConfig(
  value: unknown,
  expectedTransactionId?: string,
): PublicCheckoutConfig {
  if (!isRecord(value)) throw new Error('invalid_checkout_config');
  const environment = value.environment;
  const clientToken = value.clientToken;
  const expectedTokenPrefix = environment === 'sandbox' ? 'test_' : 'live_';
  if (
    (environment !== 'sandbox' && environment !== 'production') ||
    typeof clientToken !== 'string' ||
    !clientToken.startsWith(expectedTokenPrefix) ||
    clientToken.length < 20 ||
    clientToken.length > 512 ||
    !/^[A-Za-z0-9_-]+$/.test(clientToken)
  ) {
    throw new Error('invalid_checkout_config');
  }
  const offersValue = value.offers;
  if (!Array.isArray(offersValue) || offersValue.length !== 2) {
    throw new Error('invalid_checkout_config');
  }
  const offers = offersValue.map(parseOffer);
  if (offers[0]?.id !== 'personal' || offers[1]?.id !== 'family') {
    throw new Error('invalid_checkout_config');
  }
  const upgrade = parseOffer(value.upgrade);
  if (upgrade.id !== 'upgrade' || upgrade.kind !== 'license-upgrade') {
    throw new Error('invalid_checkout_config');
  }

  const countryCode = value.countryCode;
  if (
    countryCode !== undefined &&
    (typeof countryCode !== 'string' || !/^[A-Z]{2}$/.test(countryCode))
  ) {
    throw new Error('invalid_checkout_config');
  }

  const checkout = value.checkout === undefined ? undefined : parseCheckout(value.checkout);
  if (expectedTransactionId && checkout?.transactionId !== expectedTransactionId) {
    throw new Error('invalid_checkout_config');
  }
  if (!expectedTransactionId && checkout) throw new Error('invalid_checkout_config');

  return {
    environment,
    clientToken,
    ...(countryCode ? { countryCode } : {}),
    offers: offers as [PhotoKeeperOffer, PhotoKeeperOffer],
    upgrade,
    ...(checkout ? { checkout } : {}),
  };
}

export function formattedTotalsByPrice(response: PricePreviewResponse): Record<string, string> {
  const result: Record<string, string> = {};
  for (const item of response.data.details.lineItems) {
    result[item.price.id] = item.formattedTotals.total;
  }
  return result;
}

export function previewOffers(config: PublicCheckoutConfig): PhotoKeeperOffer[] {
  return [...config.offers, config.upgrade];
}

function parseOffer(value: unknown): PhotoKeeperOffer {
  if (!isRecord(value)) throw new Error('invalid_checkout_config');
  const id = value.id;
  const kind = value.kind;
  const accountLimit = value.accountLimit;
  if (
    (id !== 'personal' && id !== 'family' && id !== 'upgrade') ||
    (kind !== 'standalone-license' && kind !== 'license-upgrade') ||
    (accountLimit !== 1 && accountLimit !== 3) ||
    typeof value.name !== 'string' ||
    typeof value.description !== 'string' ||
    typeof value.cta !== 'string' ||
    typeof value.priceId !== 'string' ||
    !priceIdPattern.test(value.priceId) ||
    !Array.isArray(value.features) ||
    !value.features.every((feature) => typeof feature === 'string')
  ) {
    throw new Error('invalid_checkout_config');
  }
  return {
    id,
    name: value.name,
    description: value.description,
    features: value.features,
    kind,
    accountLimit,
    priceId: value.priceId,
    cta: value.cta,
  };
}

function parseCheckout(value: unknown): NonNullable<PublicCheckoutConfig['checkout']> {
  if (
    !isRecord(value) ||
    typeof value.transactionId !== 'string' ||
    !transactionIdPattern.test(value.transactionId) ||
    (value.offerId !== 'personal' && value.offerId !== 'family' && value.offerId !== 'upgrade') ||
    typeof value.expiresAt !== 'string' ||
    !Number.isFinite(Date.parse(value.expiresAt))
  ) {
    throw new Error('invalid_checkout_config');
  }
  return {
    transactionId: value.transactionId,
    offerId: value.offerId,
    expiresAt: value.expiresAt,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
